import { useState } from 'react';
import { RotateCcw, FilePlus2 } from 'lucide-react';
import { WorkflowPanel, ProductivityPanel } from '../WorkspaceSync';
import { useLanguage } from '../../context/LanguageContext';

/**
 * ChatToolbar — extraído de ChatPanel.jsx en P3.1 sesión 3/4 (feb-2026).
 *
 * Contiene los 2 sub-bloques visuales (desktop y mobile) con botones
 * de Workflow, Métricas, Rollback e Historial. JSX y clases Tailwind
 * IDÉNTICOS al original.
 *
 * iter453 (2026-02-21): añade botón "Nuevo Proyecto" visible solo con
 * activeModuleId. Al clickear dispara `damyxia:active-module-changed`
 * con `reason=user-close` para desactivar el project lock y prevenir
 * arrastre entre proyectos.
 *
 * Reglas del cuadro negro:
 * - NO toca handlers de streaming, fetch, ni envío de mensajes.
 * - Solo recibe los flags + callbacks y los pinta.
 * - Mantiene los mismos data-testid del original.
 */
const ChatToolbar = ({
  isProcessing,
  // workflow / metrics
  workflowStep,
  productivityMetrics,
  // states (desktop)
  showWorkflowDesktop,
  setShowWorkflowDesktop,
  showMetricsDesktop,
  setShowMetricsDesktop,
  // states (mobile)
  showWorkflow,
  setShowWorkflow,
  showMetrics,
  setShowMetrics,
  // rollback
  handleRevertClick,
  reverting,
  hasSnapshots = true, // 🟦 V18.7 — false ⇒ botón Revertir queda visualmente disabled.
  // history
  onOpenConversationsModal,
  // iter453 · Nuevo Proyecto (reset del active module)
  activeModuleId = null,
  activeModuleName = '',
  // i18n strings (ya formateados por el padre)
  t,
}) => {
  const { language } = useLanguage();
  // Estado local del menu mobile — vivía como `showMobileFunctions` en
  // ChatPanel; lo movemos aquí porque solo lo usa este sub-bloque.
  const [showMobileFunctions, setShowMobileFunctions] = useState(false);

  const hasActiveProject = Boolean(activeModuleId);

  const handleNewProject = () => {
    // 🟥 iter453 · Reset EXPLÍCITO del proyecto activo.
    // Dispara el evento con reason='user-close' para que useAppState
    // acepte el reset a null (los reset sin razón explícita se ignoran
    // por CUADRO NEGRO). También limpia localStorage por si algún
    // consumidor lee el ID directamente del storage.
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('damyxia:active-module-id');
      }
      if (typeof window !== 'undefined') {
        window.__damyxiaActiveModuleId = null;
        window.dispatchEvent(new CustomEvent('damyxia:active-module-changed', {
          detail: { moduleId: null, reason: 'user-close', source: 'chat-toolbar-new-project' },
        }));
      }
    } catch (_) { /* noop */ }
    setShowMobileFunctions(false);
  };

  return (
    <>
      {/* Workflow y Métricas - Colapsables en móvil y desktop */}
      {/* Versión Desktop - colapsable */}
      <div className="hidden sm:block px-3 py-2 border-b border-zinc-800/30 bg-zinc-900/20">
        {/* Botones de toggle para desktop */}
        <div className="flex gap-1.5 mb-2">
          {/* Botón Workflow Desktop */}
          <button
            onClick={() => setShowWorkflowDesktop(!showWorkflowDesktop)}
            className={`flex-1 min-w-0 flex items-center justify-between px-2 py-2 rounded-lg text-xs transition-all ${
              showWorkflowDesktop
                ? 'bg-indigo-600/20 border border-indigo-500/40 text-indigo-300'
                : 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:text-zinc-300 hover:bg-zinc-800/70'
            }`}
            data-testid="toggle-workflow-desktop"
          >
            <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span className="font-medium truncate">{t.workflow}</span>
              {isProcessing && (
                <span className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse flex-shrink-0" />
              )}
            </div>
            <svg
              className={`w-3.5 h-3.5 flex-shrink-0 ml-0.5 transition-transform duration-200 ${
                showWorkflowDesktop ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Botón Métricas Desktop */}
          <button
            onClick={() => setShowMetricsDesktop(!showMetricsDesktop)}
            className={`flex-1 min-w-0 flex items-center justify-between px-2 py-2 rounded-lg text-xs transition-all ${
              showMetricsDesktop
                ? 'bg-emerald-600/20 border border-emerald-500/40 text-emerald-300'
                : 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:text-zinc-300 hover:bg-zinc-800/70'
            }`}
            data-testid="toggle-metrics-desktop"
          >
            <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="font-medium truncate">{t.metrics}</span>
            </div>
            <svg
              className={`w-3.5 h-3.5 flex-shrink-0 ml-0.5 transition-transform duration-200 ${
                showMetricsDesktop ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Botón Rollback - 🟦 V18.7: disabled visual + tooltip cuando no hay snapshots */}
          <button
            onClick={handleRevertClick}
            disabled={reverting}
            className={`flex-1 min-w-0 px-2 py-2 rounded-lg text-xs transition-all flex items-center justify-center gap-1.5 overflow-hidden disabled:opacity-50 ${
              hasSnapshots
                ? 'dark:bg-amber-500/10 dark:hover:bg-amber-500/20 dark:border-amber-500/30 dark:hover:border-amber-500/50 dark:text-amber-400 dark:hover:text-amber-300 bg-amber-100 hover:bg-amber-200 border border-amber-400 text-amber-700'
                : 'dark:bg-zinc-800/30 dark:border-zinc-700/40 dark:text-zinc-500 bg-zinc-200 border border-zinc-300 text-zinc-500 opacity-60 cursor-not-allowed'
            }`}
            data-testid="rollback-btn"
            data-has-snapshots={hasSnapshots ? '1' : '0'}
            title={hasSnapshots ? t.restoreFromSnapshot : 'No hay snapshots disponibles para esta conversación.'}
          >
            <RotateCcw className={`w-4 h-4 flex-shrink-0 ${reverting ? 'animate-spin' : ''}`} />
            <span className="font-semibold truncate">{t.rollback}</span>
          </button>

          {/* Botón Historial */}
          <button
            onClick={() => onOpenConversationsModal?.()}
            className="flex-1 min-w-0 px-2 py-2 rounded-lg text-xs bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:text-zinc-300 hover:bg-zinc-800/70 transition-all flex items-center justify-center gap-1.5 overflow-hidden"
            data-testid="toggle-history-btn"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium truncate">
              {language === 'es' ? 'Historial' : 'History'}
            </span>
          </button>
        </div>

        {/* iter453 · Botón "Nuevo Proyecto" — solo con proyecto activo. */}
        {hasActiveProject && (
          <div className="mb-2">
            <button
              onClick={handleNewProject}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs bg-violet-600/15 hover:bg-violet-600/25 border border-violet-500/40 hover:border-violet-500/60 text-violet-300 hover:text-violet-200 transition-all"
              data-testid="new-project-btn"
              title={
                language === 'es'
                  ? `Cierra "${activeModuleName || 'el proyecto actual'}" para arrancar uno nuevo sin arrastre de contexto`
                  : `Close "${activeModuleName || 'the current project'}" to start a new one without context bleed`
              }
            >
              <FilePlus2 className="w-4 h-4 flex-shrink-0" />
              <span className="font-medium truncate">
                {language === 'es' ? 'Nuevo Proyecto' : 'New Project'}
              </span>
              {activeModuleName && (
                <span className="text-[10px] opacity-70 truncate max-w-[120px]">
                  · cerrar "{activeModuleName}"
                </span>
              )}
            </button>
          </div>
        )}

        {/* Contenido colapsable con animación */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showWorkflowDesktop || showMetricsDesktop ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="overflow-y-auto scrollbar-thin max-h-[280px]">
            {showWorkflowDesktop && (
              <div className="transition-all duration-200">
                <WorkflowPanel currentStep={workflowStep} isProcessing={isProcessing} />
              </div>
            )}
            {showMetricsDesktop && (
              <div className="transition-all duration-200">
                <ProductivityPanel metrics={productivityMetrics} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Versión Móvil - Botón único "Funciones" con menú desplegable */}
      <div className="sm:hidden px-2 py-1.5 border-b border-zinc-800/30 bg-zinc-900/20">
        <div className="relative">
          <button
            onClick={() => setShowMobileFunctions(!showMobileFunctions)}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs transition-all ${
              showMobileFunctions
                ? 'bg-indigo-600/20 border border-indigo-500/40 text-indigo-300'
                : 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:text-zinc-300'
            }`}
            data-testid="mobile-functions-btn"
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span className="font-medium">
                {language === 'es' ? 'Funciones' : 'Functions'}
              </span>
              {isProcessing && <span className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse" />}
            </div>
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${
                showMobileFunctions ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Menú desplegable */}
          {showMobileFunctions && (
            <div className="mt-1.5 p-1.5 bg-zinc-800/90 backdrop-blur-sm border border-zinc-700/50 rounded-lg space-y-1 animate-in fade-in slide-in-from-top-1 duration-200">
              {/* iter453 · Nuevo Proyecto — arriba del todo si hay proyecto activo */}
              {hasActiveProject && (
                <button
                  onClick={handleNewProject}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs text-violet-300 hover:bg-violet-500/15 transition-all"
                  data-testid="mobile-new-project-btn"
                >
                  <FilePlus2 className="w-4 h-4 flex-shrink-0" />
                  <span className="font-semibold">
                    {language === 'es' ? 'Nuevo Proyecto' : 'New Project'}
                  </span>
                  {activeModuleName && (
                    <span className="text-[10px] opacity-70 truncate ml-auto max-w-[140px]">
                      cerrar "{activeModuleName}"
                    </span>
                  )}
                </button>
              )}

              {/* 1. Historial */}
              <button
                onClick={() => {
                  onOpenConversationsModal?.();
                  setShowMobileFunctions(false);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs text-zinc-300 hover:bg-zinc-700/50 transition-all"
                data-testid="mobile-history-btn"
              >
                <svg className="w-4 h-4 text-zinc-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">
                  {language === 'es' ? 'Historial' : 'History'}
                </span>
              </button>

              {/* 2. Revertir — 🟦 V18.7 disabled visual sin snapshots */}
              <button
                onClick={handleRevertClick}
                disabled={reverting}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs transition-all disabled:opacity-50 ${
                  hasSnapshots
                    ? 'text-amber-400 hover:bg-amber-500/10'
                    : 'text-zinc-500 opacity-60 cursor-not-allowed'
                }`}
                data-testid="mobile-rollback-btn"
                data-has-snapshots={hasSnapshots ? '1' : '0'}
                title={hasSnapshots ? t.restoreFromSnapshot : 'No hay snapshots disponibles para esta conversación.'}
              >
                <RotateCcw className={`w-4 h-4 flex-shrink-0 ${reverting ? 'animate-spin' : ''}`} />
                <span className="font-semibold">{t.rollback}</span>
              </button>

              {/* 3. Flujo de trabajo */}
              <button
                onClick={() => {
                  setShowWorkflow(!showWorkflow);
                  setShowMetrics(false);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs transition-all ${
                  showWorkflow ? 'bg-indigo-600/20 text-indigo-300' : 'text-zinc-300 hover:bg-zinc-700/50'
                }`}
                data-testid="mobile-workflow-btn"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="font-medium">{t.workflowShort}</span>
                {isProcessing && (
                  <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-pulse ml-auto" />
                )}
              </button>

              {/* 4. Métricas de sesión */}
              <button
                onClick={() => {
                  setShowMetrics(!showMetrics);
                  setShowWorkflow(false);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs transition-all ${
                  showMetrics ? 'bg-emerald-600/20 text-emerald-300' : 'text-zinc-300 hover:bg-zinc-700/50'
                }`}
                data-testid="mobile-metrics-btn"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span className="font-medium">{t.metricsShort}</span>
              </button>
            </div>
          )}
        </div>

        {/* Panel Workflow expandible */}
        {showWorkflow && (
          <div className="mt-2 p-2 bg-zinc-800/30 rounded-lg border border-zinc-700/30">
            <WorkflowPanel currentStep={workflowStep} isProcessing={isProcessing} />
          </div>
        )}

        {/* Panel Métricas expandible */}
        {showMetrics && (
          <div className="mt-2 p-2 bg-zinc-800/30 rounded-lg border border-zinc-700/30">
            <ProductivityPanel metrics={productivityMetrics} />
          </div>
        )}
      </div>
    </>
  );
};

export default ChatToolbar;
